import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { allProductsAction } from "../../../redux/actions/productActions";
import LoaderBlock from "../../loader/LoaderBlock";
import SingleProduct from "../SingleProduct";
import { toast } from "react-toastify";
import useNavHighlight from "../../../hooks/useNavHighlight";
import usePageMeta from "../../../hooks/usePageMeta";
import ProductFilters from "./ProductFilters";
import Pagination from "../../shared/Pagination";
import normalizeUpc from "../../../utils/normalizeUpc";

const toSearchUpc = (raw) => {
    const trimmed = String(raw || "").trim();
    if (!trimmed) return "";
    return normalizeUpc(trimmed) || trimmed;
};

const ProductCatalog = () => {
    const dispatch = useDispatch();

    const { loading, products, totalPages, totalProducts } = useSelector(state => state.products);
    const { error, message } = useSelector(state => state.productMessage);

    const [page, setPage] = useState(1);
    const [scan_code, setScan_code] = useState('');
    const [description, setDescription] = useState('');
    const [department, setDepartment] = useState('');
    const [pricing_filter, setPricing_filter] = useState('');

    useNavHighlight("/product-catalog");
    usePageMeta("productCatalog");

    const buildQuery = useCallback(() => {
        const query = {};

        if (scan_code.trim()) {
            query.scan_code = toSearchUpc(scan_code);
        }
        if (description) query.description = description;
        if (department) query.department = department;
        if (pricing_filter) query.pricing_filter = pricing_filter;

        return query;
    }, [scan_code, description, department, pricing_filter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            dispatch(allProductsAction(page, buildQuery()));
        }, 400);

        return () => clearTimeout(timer);
    }, [page, dispatch, buildQuery]);

    useEffect(() => {
        if (error) {
            toast.error(error);
        }
        if (message) {
            toast.success(message);
            dispatch(allProductsAction(page, buildQuery()));
        }
    }, [message, error, dispatch, page, buildQuery]);

    return (
        <Fragment>
            <section className="pl-[1.5vmax] pr-[2.5vmax]">
                <div className="flex justify-between items-center bg-white p-[1.2vmax] rounded-[1vmax] px-[2vmax]">
                    <h2 className="text-[1.3vmax] font-bold">Product Catalog</h2>
                    <div className="flex-center gap-[0.8vmax] text-[1vmax]">
                        <Link to={"/"}>Dashboard</Link>
                        <div className="w-[0.3vmax] h-[0.3vmax] rounded-[50%] bg-black"></div>
                        <span className="text-slate-400">Product Catalog</span>
                    </div>
                </div>
            </section>

            <section className="pl-[1.5vmax] pr-[2.5vmax] mt-[1vmax]">
                <div className="p-[2vmax] bg-white shadow-1 rounded-2xl pb-0">
                    <div className="flex justify-between items-center mb-[1vmax]">
                        <h2 className="text-[1.2vmax] font-bold">Store Price Book</h2>
                        {totalProducts > 0 && (
                            <span className="text-[0.9vmax] text-slate-500">
                                {totalProducts.toLocaleString()} total products
                            </span>
                        )}
                    </div>
                    <ProductFilters
                        scan_code={scan_code}
                        setScan_code={setScan_code}
                        description={description}
                        setDescription={setDescription}
                        department={department}
                        setDepartment={setDepartment}
                        pricing_filter={pricing_filter}
                        setPricing_filter={setPricing_filter}
                        setPage={setPage}
                    />

                    <div className="mt-[2vmax] w-full overflow-x-auto my-scroll pb-[2vmax]">
                        {loading ? (
                            <LoaderBlock />
                        ) : (
                            <>
                                {!Array.isArray(products) || products.length === 0 ? (
                                    <div className="text-center text-[2vmax] py-[3vmax]">
                                        No Product Found
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex w-[120%] 2xl:w-full bg-slate-100">
                                            <div className="flex-[0.8] flex items-center justify-center p-[0.4vmax] border-[0.1vmax] border-gray-300 text-[1vmax] font-[600] 2xl:text-[0.7vmax] text-slate-700">No.</div>
                                            <div className="flex-[1.6] flex items-center justify-center p-[0.4vmax] border-[0.1vmax] border-gray-300 text-[1vmax] font-[600] 2xl:text-[0.7vmax] text-slate-700">Scan Code</div>
                                            <div className="flex-[2] flex items-center justify-center p-[0.4vmax] border-[0.1vmax] border-gray-300 text-[1vmax] font-[600] 2xl:text-[0.7vmax] text-slate-700">Description</div>
                                            <div className="flex-[1] flex items-center justify-center p-[0.4vmax] border-[0.1vmax] border-gray-300 text-[1vmax] font-[600] 2xl:text-[0.7vmax] text-slate-700">Unit/Case</div>
                                            <div className="flex-[2] flex items-center justify-center p-[0.4vmax] border-[0.1vmax] border-gray-300 text-[1vmax] font-[600] 2xl:text-[0.7vmax] text-slate-700">Department</div>
                                            <div className="flex-[1] flex items-center justify-center p-[0.4vmax] border-[0.1vmax] border-gray-300 text-[1vmax] font-[600] 2xl:text-[0.7vmax] text-slate-700">Purchase QTY</div>
                                            <div className="flex-[1] flex items-center justify-center p-[0.4vmax] border-[0.1vmax] border-gray-300 text-[1vmax] font-[600] 2xl:text-[0.7vmax] text-slate-700">Sold QTY</div>
                                            <div className="flex-[1] flex items-center justify-center p-[0.4vmax] border-[0.1vmax] border-gray-300 text-[1vmax] font-[600] 2xl:text-[0.7vmax] text-slate-700">Adjust QTY</div>
                                            <div className="flex-[1] flex items-center justify-center p-[0.4vmax] border-[0.1vmax] border-gray-300 text-[1vmax] font-[600] 2xl:text-[0.7vmax] text-slate-700">Current QTY</div>
                                            <div className="flex-[1] flex items-center justify-center p-[0.4vmax] border-[0.1vmax] border-gray-300 text-[1vmax] font-[600] 2xl:text-[0.7vmax] text-slate-700">Each Cost</div>
                                            <div className="flex-[1] flex items-center justify-center p-[0.4vmax] border-[0.1vmax] border-gray-300 text-[1vmax] font-[600] 2xl:text-[0.7vmax] text-slate-700">Each Retail</div>
                                            <div className="flex-[1.5] flex items-center justify-center p-[0.4vmax] border-[0.1vmax] border-gray-300 text-[1vmax] font-[600] 2xl:text-[0.7vmax] text-slate-700">Total Cost</div>
                                            <div className="flex-[1.5] flex items-center justify-center p-[0.4vmax] border-[0.1vmax] border-gray-300 text-[1vmax] font-[600] 2xl:text-[0.7vmax] text-slate-700">Total Retail</div>
                                            <div className="flex-[1.2] flex items-center justify-center p-[0.4vmax] border-[0.1vmax] border-gray-300 text-[1vmax] font-[600] 2xl:text-[0.7vmax] text-slate-700">Action</div>
                                        </div>
                                        {products.map((product, index) => (
                                            <SingleProduct
                                                key={product.id ?? `${product.scan_code}-${index}`}
                                                product={product}
                                                index={(totalProducts || 0) - (index + ((page - 1) * 20))}
                                            />
                                        ))}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </section>

            <Pagination page={page} totalPages={totalPages} setPage={setPage} />

            <br />
            <br />
            <br />
            <br />
            <br />
        </Fragment>
    );
};

export default ProductCatalog;
